
































































import {mixin as clickaway} from 'vue-clickaway';
import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {AvailableTriggers, TriggerType} from '@/features/bots/models/designer';
import {
	SET_TRIGGER,
	UPDATE_SCHEDULE_CONDITION,
	UPDATE_TRIGGER_CONDITIONS,
	UPDATE_AUDIENCE_CONDITIONS,
	UPDATE_TARGET_CONTACT_TYPES
} from '@/store/bot-designer/triggers/mutationTypes';
import {Action, Getter, Mutation} from 'vuex-class';
import TriggerScheduler from './TriggerScheduler.vue';
import IslandBadge from './IslandBadge.vue';
import AdditionalInformationWrapper from './AdditionalInformationWrapper.vue';
import ErrorIcon from 'vue-material-design-icons/AlertCircleOutline.vue';
import HelpIcon from 'vue-material-design-icons/HelpCircleOutline.vue';
import Triggers from '@/features/bots/components/designer/Triggers.vue';
import {BusinessHoursViewModel} from '../../models';
import {TriggerValidity} from '@/features/bots/models/designer/triggerValidity';
import {NLP_ENABLED} from '@/store/app/featureTypes';
import {ConditionGroupViewModel} from '@/shared/components/conditions/models/conditionGroupViewModel';
import {ConditionViewModel} from '@/shared/components/conditions/models/conditionViewModel';
import {ConditionGroup} from '@/shared/components/conditions/models/conditionGroup';
import ConditionGroupBuilder, { ConditionGroupUpdate } from '@/shared/components/conditions/ConditionGroupBuilder.vue';
import {TriggerConditionsManager, TriggerConditionAttributeType} from './Conditions/triggerConditionsManager';
import {AudienceConditionsManager} from './Conditions/audienceConditionsManager';
import {ContactType} from '@/features/contacts/models/contactType';
import MultiContactTypePicker from '@/features/bots/components/designer/MultiContactTypePicker.vue';
import {ChannelIdentityViewModel} from '@/messaging/models/channelIdentityViewModel';
import { GET_INSTALLED_APPS } from '@/store/app/actionTypes';
import { PROACTIVE_FLOWS_ENABLED } from '@/store/app/featureTypes';

@Component({
	mixins: [
		clickaway
	],
	components: {
		TriggerScheduler,
		IslandBadge,
		AdditionalInformationWrapper,
		ErrorIcon,
		HelpIcon,
		Triggers,
		ConditionGroupBuilder,
		MultiContactTypePicker
	}
})
export default class TriggerBox extends Vue {
	@Getter public triggerValidity: TriggerValidity;
	@Getter public isWorkspaceFeatureEnabled!: (key: string) => boolean;
	@Getter public channelIdentities: ChannelIdentityViewModel[];
	@Getter public isAppInstalled!: (appId: string) => boolean;
	@Getter public hasInstalledApps!: boolean;
	@Prop() public value: string;
	@Prop() public triggerConditions: ConditionGroupViewModel;
	@Prop() public audienceConditions: ConditionGroupViewModel;
	@Prop() public contactTypes: string[];
	@Prop() public schedule: ConditionViewModel;
	@Prop() public openingTimes: BusinessHoursViewModel[];
	@Mutation(SET_TRIGGER) public setTrigger: (trigger: string) => void;
	@Mutation(UPDATE_TRIGGER_CONDITIONS) public updateTriggerConditions: (update: any) => void;
	@Mutation(UPDATE_AUDIENCE_CONDITIONS) public updateAudienceConditions: (update: any) => void;
	@Mutation(UPDATE_SCHEDULE_CONDITION) public updateScheduleCondition: (update: any) => void;
	@Mutation(UPDATE_TARGET_CONTACT_TYPES) public updateContactTypes: (update: string[]) => void;
	@Action(GET_INSTALLED_APPS) public getInstalledApps: () => Promise<void>;
	public triggerType: string = '';
	public scheduleCondition: ConditionViewModel | null = null;
	public showConfig: boolean = false;
	public excludedTriggerTypes: string[] = [];

	public triggerConditionList: ConditionGroupViewModel = {
		type: 'and',
		conditions: []
	};
	public audienceConditionList: ConditionGroupViewModel = {
		type: 'and',
		conditions: []
	};

	public triggerConditionsManager: TriggerConditionsManager = new TriggerConditionsManager();
	public audienceConditionsManager: AudienceConditionsManager = new AudienceConditionsManager();

	public isAddNewPickerOpen = false;

	public availableTypes = AvailableTriggers.list();

	@Watch('value', { immediate: true })
	public onValueChanged(value: string, previous: string) {
		this.triggerType = value;
	}

	@Watch('triggerConditions', { immediate: true })
	public onTriggerConditionsChanged(value: ConditionGroupViewModel, previous: ConditionGroupViewModel) {
		this.triggerConditionList = value;
	}

	@Watch('audienceConditions', { immediate: true })
	public onAudienceConditionsChanged(value: ConditionGroupViewModel, previous: ConditionGroupViewModel) {
		this.audienceConditionList = value;
	}

	@Watch('schedule', { immediate: true })
	public onScheduleChanged(value: ConditionViewModel, previous: ConditionViewModel) {
		this.scheduleCondition = value;
	}

	public mounted() {
		this.getExcludedTriggerTypes();
		this.triggerConditionsManager.excludeConditions(this.getExcludedConditions());
	}

	public triggerConditionsUpdated(update: ConditionGroupUpdate) {
		this.triggerConditionsManager.excludeConditions(this.getExcludedConditions());
		this.updateTriggerConditions(update.conditionGroup);
	}

	public audienceConditionsUpdated(update: ConditionGroupUpdate) {
		this.triggerConditionsManager.excludeConditions(this.getExcludedConditions());
		this.updateAudienceConditions(update.conditionGroup);
	}

	public contactTypeUpdated(newContactTypes: ContactType[]) {
		this.updateContactTypes(newContactTypes);
	}

	public closeAddNewPicker() {
		this.isAddNewPickerOpen = false;
	}

	public showTriggerConfig() {
		this.showConfig = true;
	}

	public hideTriggerConfig() {
		this.showConfig = false;
		this.closeAddNewPicker();
	}

	public isTriggerSelected(type: TriggerType) {
		return this.triggerType === type;
	}

	public selectTriggerType(type: TriggerType) {
		this.handleSwitchingTrigger(type);
		if (this.triggerType === type) {
			this.triggerType = '';
		} else {
			this.triggerType = type;
		}
		this.setTrigger(this.triggerType);
		this.triggerConditionsManager.excludeConditions(this.getExcludedConditions());
	}

	public onScheduleUpdated(condition: ConditionViewModel, onlineTimes: any) {
		this.updateScheduleCondition({condition, openingTimes: onlineTimes.customTimes});
	}

	public async getIsGoogleAppInstalled(): Promise<boolean> {
		if (!this.hasInstalledApps) {
			await this.getInstalledApps();
		}
		return this.isAppInstalled('google-bm');
	}

	public async getExcludedTriggerTypes() {
		const isGoogleAppInstalled = await this.getIsGoogleAppInstalled();
		if (!isGoogleAppInstalled) {
			this.excludedTriggerTypes.push(TriggerType.AgentRequested);
		}
		if (!this.isProactiveFlowsEnabled()) {
			this.excludedTriggerTypes.push(TriggerType.WidgetLoaded);
		}
	}

	public get availableTriggerTypes() {
		return this.availableTypes.filter(trigger => !this.excludedTriggerTypes.includes(trigger.type));
	}

	public isProactiveFlowsEnabled() {
		return this.isWorkspaceFeatureEnabled(PROACTIVE_FLOWS_ENABLED);
	}

	public getExcludedConditions() {
		const excludedConditions: string[] = [];
		if (!this.isTriggerSelected(TriggerType.MessageReceived)) {
			excludedConditions.push(TriggerConditionAttributeType.MessageText);
			excludedConditions.push(TriggerConditionAttributeType.MessageIntent);
		} else if (!this.isWorkspaceFeatureEnabled(NLP_ENABLED)) {
			excludedConditions.push(TriggerConditionAttributeType.MessageIntent);
		} else {
			if (this.triggerConditionList) {
				const messageCondition = ConditionGroup.find(this.triggerConditionList, (c => c.attribute === TriggerConditionAttributeType.MessageText));
				if (messageCondition) {
					excludedConditions.push(TriggerConditionAttributeType.MessageIntent);
				}

				const intentCondition = ConditionGroup.find(this.triggerConditionList, (c => c.attribute === TriggerConditionAttributeType.MessageIntent));
				if (intentCondition) {
					excludedConditions.push(TriggerConditionAttributeType.MessageText);
					excludedConditions.push(TriggerConditionAttributeType.MessageIntent);
				}
			}
		}
		if (this.isTriggerSelected(TriggerType.WidgetLoaded) || this.isTriggerSelected(TriggerType.ConversationOpened) || this.channelIdentities.length === 0 || !this.isWorkspaceFeatureEnabled('multipleIdentitiesPerChannelEnabled')) {
			excludedConditions.push(TriggerConditionAttributeType.ChannelIdentity);
		}

		if (!this.isTriggerSelected(TriggerType.WidgetLoaded)) {
			excludedConditions.push(TriggerConditionAttributeType.TimeOnPage);
		} else {
			if (this.triggerConditionList) {
				const timeOnPageCondition = ConditionGroup.find(this.triggerConditionList, (c => c.attribute === TriggerConditionAttributeType.TimeOnPage));
				if (timeOnPageCondition) {
					excludedConditions.push(TriggerConditionAttributeType.TimeOnPage);
				}
			}
		}
		return excludedConditions;
	}

	public getTooltipForTrigger(type: TriggerType) {
		if (type === TriggerType.MessageReceived) {
			return `A message is received and the user's intention is recognised`;
		} else if (type === TriggerType.Unknown) {
			return `A message is received and the user's intention is not recognised`;
		} else if (type === TriggerType.ConversationOpened) {
			return `When the user opens the Web Assistant conversation`;
		} else if (type === TriggerType.WidgetLoaded) {
			return `User opens a webpage with Web Assistant installed`;
		}
	}

	private handleSwitchingTrigger(type: string) {
		const invalidAttributes: string[] = [];
		if (type !== TriggerType.MessageReceived) {
			invalidAttributes.push(TriggerConditionAttributeType.MessageText);
			invalidAttributes.push(TriggerConditionAttributeType.MessageIntent);
		}

		if (type !== TriggerType.WidgetLoaded) {
			invalidAttributes.push(TriggerConditionAttributeType.TimeOnPage);
		}

		if (type !== TriggerType.MessageReceived && type !== TriggerType.Unknown) {
			invalidAttributes.push(TriggerConditionAttributeType.ChannelIdentity);
		}

		if (invalidAttributes.length > 0) {
			this.triggerConditionList = this.removeInvalidConditions(this.triggerConditionList, invalidAttributes);
			this.updateTriggerConditions(this.triggerConditionList);
		}
	}

	private removeInvalidConditions(conditionGroup: ConditionGroupViewModel, invalidAttributes: string[]): ConditionGroupViewModel {
		for (let i = conditionGroup.conditions.length - 1; i >= 0; i--) {
			const c = conditionGroup.conditions[i];
			if (ConditionGroup.isCondition(c)) {
				if (invalidAttributes.includes(c.attribute)) {
					conditionGroup.conditions.splice(i, 1);
				}
			} else {
				this.removeInvalidConditions(c, invalidAttributes);
			}
		}

		return conditionGroup;
	}
}
